import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

import { useListParams } from "hooks/useListParams"
import { useAccountExpenses, useAccountExpense, useDeleteAccountExpense, useApproveAccountExpense, useRejectAccountExpense } from "hooks/useAccountExpense"
import { useAccountExpenseTypes } from "hooks/useAccountExpenseType"
import { useAccounts } from "hooks/useAccountEntity"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { downloadAndOpenFile } from "api/pdfApi"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"

import AccountExpenseAddEdit from "./AccountExpenseAddEdit"
import AccountExpenseHeader from "./AccountExpenseHeader"
import AccountExpenseView from "./AccountExpenseView"

const AccountExpenseList = () => {
  document.title = "Account Expense List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", expense_type_id: "", account_id: "" })
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingCsv, setIsExportingCsv] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", expense_type_id: "", account_id: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
      ...(filters.expense_type_id && { expense_type_id: filters.expense_type_id }),
      ...(filters.account_id && { account_id: filters.account_id }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountExpenses(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountExpense(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountExpense()
  const { mutateAsync: approveItem } = useApproveAccountExpense()
  const { mutateAsync: rejectItem } = useRejectAccountExpense()

  const handleApprove = async id => {
    try {
      const res = await approveItem(id)
      toast.success(res?.message ?? "Expense approved successfully.")
      refetch()
    } catch {
      toast.error("Failed to approve expense.")
    }
  }

  const handleReject = async (id, reason) => {
    try {
      const res = await rejectItem({ id, rejection_reason: reason })
      toast.success(res?.message ?? "Expense rejected.")
      refetch()
    } catch {
      toast.error("Failed to reject expense.")
    }
  }

  const handleExportPdf = () =>
    downloadAndOpenFile({ url: "/account-exports/expenses", params: queryParams, openInNewTab: false, filename: "expenses.pdf", onStart: () => setIsExporting(true), onEnd: () => setIsExporting(false) })

  const handleExportCsv = () =>
    downloadAndOpenFile({ url: "/account-exports/expenses/csv", params: queryParams, mimeType: "text/csv", openInNewTab: false, filename: "expenses.csv", onStart: () => setIsExportingCsv(true), onEnd: () => setIsExportingCsv(false) })

  const { data: expenseTypeResponse } = useAccountExpenseTypes({ all: true, per_page: 200 })
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 })
  const expenseTypeList = expenseTypeResponse?.data?.data || expenseTypeResponse?.data || []
  const accountList = accountResponse?.data?.data || accountResponse?.data || []

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Expense deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      toast.error("Failed to delete expense.")
    }
  }

  const columns = useMemo(
    () => [
      { id: "id", header: "ID", accessorKey: "id" },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => row.original.expense_type?.name || row.original.expenseType?.name || "-",
      },
      {
        id: "account",
        header: "Account",
        cell: ({ row }) => row.original.account?.name || "-",
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => {
          const currency = row.original.account?.currency || "BDT"
          return (
            <div className="d-flex flex-wrap gap-1 align-items-center">
              <Badge color="light" className="text-dark">{formatCurrency(row.original.amount || 0, currency)}</Badge>
              {row.original.is_reversed && <Badge color="secondary">Reversed</Badge>}
            </div>
          )
        },
      },
      {
        id: "expense_date",
        header: "Expense Date",
        cell: ({ row }) =>
          row.original.expense_date ? formatReadableDate(row.original.expense_date) : "-",
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-expense-${row.original.id}`}>
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`view-account-expense-${row.original.id}`}>View</UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.ACCOUNT_EXPENSE.EDIT]}>
              <li>
                <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-account-expense-${row.original.id}`}>
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-account-expense-${row.original.id}`}>Edit</UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_EXPENSE.DELETE]}>
              <li>
                <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-expense-${row.original.id}`}>
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`delete-account-expense-${row.original.id}`}>Delete</UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Expense" breadcrumbItem="Account Expense List">
      <AccountExpenseHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
        onExportPdf={handleExportPdf}
        isExporting={isExporting}
        onExportCsv={handleExportCsv}
        isExportingCsv={isExportingCsv}
      />

      <div className="pb-3" />

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Expense Type</Label>
          <Input type="select" bsSize="sm" value={filters.expense_type_id || ""} onChange={e => handleFilterChange("expense_type_id", e.target.value)}>
            <option value="">All Types</option>
            {expenseTypeList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Account</Label>
          <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
            <option value="">All Accounts</option>
            {accountList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Input>
        </Col>
      </AccountFilterPanel>

      <Row className="mb-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      <AccountExpenseView modal={viewModal} toggleModal={toggleViewModal} expense={viewResponse?.data} loading={isViewLoading} onApprove={handleApprove} onReject={handleReject} />

      <AccountExpenseAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountExpenseList
